import React, { useContext, useEffect, useState } from 'react';
import { ContentChart } from '@meteor/frontend-core';
import { AppContext } from '../../../../contexts/AppContext';
import { GLOBAL_EVENT } from '../../../../consts/common/global-event';
import APIList from '../../../../http/ApiList';
import { cloneDeep, isString } from 'lodash';
import ErrorBoundary from '../../../common/error-boundary';

const AnimationScatterChart: React.FC<any> = (props) => {
  const { authInfo, echartTheme, globalEvent, setGlobalEvent, color } = useContext(AppContext);
  const { property } = props;
  const [chartLoading, setChartLoading] = useState<any>(false);
  const [chartData, setChartData] = useState<any>(null);
  let axisKeys = [];
  const defaultChartOptions: any = {
    tooltip: {
      formatter: (params) => {
        return (
          `${params.marker}${params.seriesName}` +
          '<br/>' +
          `営業利益: ${params.data[0]}` +
          '<br/>' +
          `前年比伸び率: ${(params.data[1] * 100).toFixed(2)}%` +
          '<br/>' +
          `売上: ${params.data[2]}`
        );
      },
    },
    legend: {
      top: 0,
      type: 'scroll',
      width: '70%',
    },
    grid: {
      bottom: 50,
      top: 30,
      right: 60,
      left: 50,
    },
    yAxis: {
      type: 'value',
      name: '前年度伸び率',
      nameTextStyle: {
        fontSize: 16,
      },
      max: 0.08,
      axisLabel: {
        formatter: (text) => {
          return text * 100 + '%';
        },
      },
    },
    xAxis: {
      name: '営業利益',
      nameLocation: 'center',
      nameGap: 25,
      type: 'value',
      axisLabel: {},
      nameTextStyle: {
        fontSize: 16,
      },
      min: 0,
      max: 1000,
    },

    animationDurationUpdate: 1000,
    animationEasingUpdate: 'quinticInOut',
    timeline: {
      axisType: 'category',
      orient: 'vertical',
      autoPlay: false,
      inverse: true,
      playInterval: 3000,
      left: null,
      right: 0,
      top: 30,
      bottom: 70,
      width: 45,
      height: null,
      currentIndex: 1,
      checkpointStyle: {
        borderWidth: 2,
        symbolSize: 10,
        color: color.primaryColor,
      },
      controlStyle: {
        showNextBtn: false,
        showPrevBtn: false,
        itemSize: 16,
        borderColor: color.primaryColor,
        color: color.primaryColor,
        shadowColor: color.primaryColor,
      },
      itemStyle: {
        color: color.tertiaryColor,
      },
      lineStyle: { color: color.tertiaryColor },
      symbolSize: 8,
      progress: {
        lineStyle: { color: color.primaryColor },
        itemStyle: { color: color.primaryColor },
      },
      data: ['FY23', 'FY24', 'FY25'],
    },
  };
  defaultChartOptions.series = [
    {
      type: 'scatter',
      name: 'newScatter',
    },
  ];
  const [options, setOptions] = useState<any>(defaultChartOptions);

  const getScatterData = (dataArray, keys) => {
    let seriesArray = keys.map((k) => {
      let seriesData = dataArray[k].map((d) => {
        let seriesData = [];
        let data1 = axisKeys.map((a) => {
          return d[a];
        });
        seriesData.push(data1);
        return {
          data: seriesData,
          symbolSize: (d) => {
            return d[2] * 0.08;
          },
          label: {
            show: true,
            color: '#000',
            formatter: (params) => {
              return params.seriesName;
            },
          },
        };
      });
      return {
        series: seriesData,
      };
    });

    return seriesArray;
  };

  const getChartData = (event: string) => {
    APIList.getCustomizeLayoutData()
      .post({ email: authInfo?.user?.attributes?.email || '', property, event })
      .then((res: any) => {
        setChartData(res.data);
        setChartLoading(false);
      });
  };

  useEffect(() => {
    if (!property || !property.chartOptions || !property.chartOptions.options) {
      return;
    }
    try {
      let op;
      if (property.chartOptions.options) {
        const reviver = (key, value) => {
          const arrowFunctionRegex = /^\s*\(?\s*([^)]*)\s*\)?\s*=>\s*{[^]*}$/;
          if (typeof value === 'string' && (value.startsWith('function') || arrowFunctionRegex.test(value))) {
            return new Function('return ' + value)();
          }
          return value;
        };
        const tempOp = JSON.parse(property.chartOptions.options, reviver);
        op = {
          ...options,
          ...tempOp,
        };
      } else {
        op = defaultChartOptions;
      }
      setOptions(cloneDeep({ ...op, echartTheme }));
    } catch (error) {}
  }, [property]);

  useEffect(() => {
    if (!globalEvent) {
      return;
    }
    if (
      (property.dataSource &&
        property.dataSource.triggerEvent &&
        property.dataSource.triggerEvent.find((triggerEvent) => {
          return triggerEvent.event === globalEvent.eventType;
        })) ||
      globalEvent.eventType === GLOBAL_EVENT.PAGE_INIT
    ) {
      setChartLoading(true);
      setTimeout(() => {
        getChartData(globalEvent);
      }, 500);
    }
  }, [globalEvent]);

  const getRangeValue = (range) => {
    if (isString(range)) {
      let reg = /[\d,\d]/;
      if (reg.test(range)) {
        return range.substring(1, range.length - 1).split(',');
      }
    }
    return false;
  };
  const setXYRange = () => {
    let xRange = getRangeValue(chartData.xRange);
    if (xRange) {
      options.xAxis.max = xRange[1];
      options.xAxis.min = xRange[0];
    }

    let yRange = getRangeValue(chartData.yRange);
    if (yRange) {
      options.yAxis.max = yRange[1];
      options.yAxis.min = yRange[0];
    }
  };

  useEffect(() => {
    if (!chartData || chartData.length === 0) {
      return;
    }
    // options.options
    options.series = chartData.seriesKeys.map((sk) => {
      return { type: 'scatter', name: sk };
    });
    options.timeline.data = chartData.stepKeys;
    axisKeys = chartData.axisKeys;
    options.options = getScatterData(chartData.data, chartData.stepKeys);
    setXYRange();
    setOptions(cloneDeep({ ...options, echartTheme }));
  }, [chartData]);

  return (
    <ErrorBoundary>
      <ContentChart
        title={{
          value: (
            <>
              <span>{property.title}</span> <span style={{ float: 'right' }}>{property.subtitle}</span>
            </>
          ),
          styles: {},
        }}
        contentList={[]}
        chartOptions={options}
        showSeriesLabel={false}
        mouseWheelZoomLock={false}
        loading={chartLoading}
        height={'90%'}
        tooltips={['zoomIn']}
        isBank={!chartData || chartData.length === 0}
        onChartEvent={setGlobalEvent}
      />
    </ErrorBoundary>
  );
};

export default AnimationScatterChart;
